import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Trophy,
  Briefcase,
  MapPin,
  Plane,
  Waves,
} from "lucide-react";
import Seo from "@/components/Seo";
import { DESTINATION_IMAGES } from "@/lib/media";

const BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "Philippines DMC", path: "/philippines-dmc" },
];

const SERVICES = [
  {
    icon: Trophy,
    title: "Island Incentive Programmes",
    copy:
      "Private island buyouts, beachfront gala dinners, helicopter island-hopping, banca-boat charters and the world's most photogenic beach experiences.",
  },
  {
    icon: Briefcase,
    title: "Corporate Group Travel",
    copy:
      "End-to-end programme management across Manila, Cebu and resort destinations — flights, hotel blocks, transfers, F&B and 24/7 ground operations.",
  },
  {
    icon: Trophy,
    title: "Adventure & Team-Building",
    copy:
      "Scuba diving on Apo Reef, underground river kayaking in Palawan, island survival challenges, dragon-boat regattas and amazing-race-style city challenges.",
  },
  {
    icon: Waves,
    title: "MICE & Conferences",
    copy:
      "Conference production at the SMX Convention Centre Manila, World Trade Center Manila, and luxury resort venues across the Philippines.",
  },
];

const CITIES = [
  {
    name: "Manila",
    note:
      "The Philippines' gateway city. International 5-star hotels — Shangri-La at the Fort, Conrad, Solaire, City of Dreams — anchor a CBD that handles MICE events of 1,000+ pax. Direct flights from across Asia, the Middle East and the US.",
  },
  {
    name: "Cebu",
    note:
      "Beach incentive heaven with international airlift. Shangri-La Mactan, Crimson Resort, and the Plantation Bay resort offer luxury beach buyouts just 30 minutes from a major international airport.",
  },
  {
    name: "Palawan & Boracay",
    note:
      "Two of the world's most-awarded beach destinations. El Nido and Coron in Palawan offer dramatic limestone karst seascapes. Boracay's powder-white sand is iconic. Top-tier resorts: Amanpulo, El Nido Resorts, Shangri-La Boracay.",
  },
];

const VENUES = [
  { name: "SMX Convention Center Manila", note: "26,000 sqm — Philippines' largest MICE venue" },
  { name: "World Trade Center Manila", note: "Twin 10,000-sqm exhibition halls" },
  { name: "Shangri-La The Fort, Manila", note: "Premium ballrooms in BGC business district" },
  { name: "Amanpulo (Pamalican Island)", note: "Private island buyout for top-tier incentive retreats" },
  { name: "Shangri-La Mactan, Cebu", note: "Beachfront resort 30 min from Cebu international airport" },
  { name: "El Nido Resorts", note: "Eco-luxury island properties — Pangulasian, Lagen, Miniloc" },
];

const FAQS = [
  {
    q: "Why choose the Philippines for an island incentive programme?",
    a: "The Philippines offers 7,641 islands — more variety, more drama, and more genuinely 'wow' beach scenery than any competing Southeast Asian destination. Combined with an English-speaking workforce, world-renowned hospitality and competitive rates, the Philippines consistently outperforms for incentive groups looking for an unforgettable beach narrative.",
  },
  {
    q: "Can Trawelldmc deliver multi-island Philippines programmes?",
    a: "Yes — multi-island Philippines (Manila to Cebu to Palawan, for example) is one of our most-requested formats. We coordinate domestic flights on Philippine Airlines and Cebu Pacific, banca-boat island transfers, hotel-to-hotel handovers, English-speaking guides at each destination, and one named programme manager for end-to-end continuity.",
  },
  {
    q: "What is the best time of year for a Philippines incentive trip?",
    a: "November to May is the prime window — dry, sunny and stable. December to early March is peak season for beach destinations like Palawan and Boracay. We generally avoid June–October due to monsoon and typhoon risk, though Cebu's geographic position makes it the most year-round-friendly Philippines destination.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function PhilippinesDmc() {
  return (
    <div data-testid="philippines-dmc-page">
      <Seo
        path="/philippines-dmc"
        title="DMC Philippines — Island Incentive & Group Travel | Trawelldmc"
        description="Expert DMC in the Philippines. Manila, Cebu & Palawan corporate travel, incentive programmes, and island experiences. Trawelldmc — 20+ years."
        breadcrumb={BREADCRUMB}
        extraJsonLd={FAQ_JSONLD}
      />

      <section className="relative">
        <img
          src={DESTINATION_IMAGES.philippines}
          alt="Philippines island incentive travel — Trawelldmc DMC Philippines"
          width="1920"
          height="1080"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(11,25,44,0.92) 0%, rgba(11,25,44,0.55) 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-32 md:py-44 text-white">
          <div className="max-w-3xl">
            <div className="overline !text-[#FF5238] mb-5">
              Philippines DMC · Manila · Cebu · Palawan
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.06]">
              <span className="text-[#FF5238]">Philippines</span> Destination Management Company (DMC) & MICE Specialist
            </h1>
            <p className="text-white/80 mt-8 text-lg max-w-2xl leading-relaxed">
              Island incentive programmes, corporate group travel and MICE
              across the world's most photogenic archipelago — engineered by
              a team with two decades on the ground.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/inquiry?destination=Philippines" className="btn-primary" data-testid="ph-cta-proposal">
                Get a Philippines proposal <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/quote" className="btn-secondary" data-testid="ph-cta-ai-quote">Instant AI estimate</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Why the Philippines</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              7,641 islands. <br />Infinite incentive stories.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-slate-700 text-lg leading-relaxed">
            <p>
              The Philippines is Southeast Asia's most under-utilised
              incentive destination. With 7,641 islands, an English-speaking
              workforce, world-renowned hospitality and dramatic beach
              scenery you simply cannot find elsewhere, the country delivers
              programme storylines that competitors cannot replicate. Palawan
              has been voted the world's best island multiple times by Travel
              + Leisure and Condé Nast readers. Boracay's powder-white sand
              is iconic. Cebu offers world-class beach inventory just 30
              minutes from a major international airport.
            </p>
            <p>
              For corporate buyers, the Philippines offers a rare combination
              — luxury 5-star inventory at 30–40% better value than comparable
              Asian destinations, an English-speaking service workforce that
              eliminates language barriers, and adventure and team-building
              options (scuba on Apo Reef, underground river kayaking,
              island-hopping by banca boat) that no other destination can
              match. The infrastructure has caught up — major brands like
              Shangri-La, Conrad, Aman, Banyan Tree and Six Senses now anchor
              a premium offering.
            </p>
            <p>
              Trawelldmc has operated the Philippines for two decades. Our
              local team owns every contract, every transfer, every venue
              inspection. From domestic flight coordination across the
              country's complex inter-island network to banca-boat charters,
              private island buyouts and English-speaking dive masters,
              we don't outsource what matters.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline mb-4">Where we work</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">Our Philippines footprint.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {CITIES.map((c) => (
              <div key={c.name} className="card-flat p-8">
                <MapPin className="h-8 w-8 text-[#FF5238]" />
                <h3 className="font-heading text-xl font-medium tracking-tight text-[#0B192C] mt-5">{c.name}</h3>
                <p className="text-slate-600 mt-3 leading-relaxed">{c.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">What we deliver</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">Our Philippines DMC services.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SERVICES.map((s) => (
            <div key={s.title} className="card-flat p-8">
              <s.icon className="h-8 w-8 text-[#FF5238]" />
              <h3 className="font-heading text-xl font-medium tracking-tight text-[#0B192C] mt-5">{s.title}</h3>
              <p className="text-slate-600 mt-3 leading-relaxed">{s.copy}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#0B192C] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline !text-[#FF5238] mb-4">Top venues</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight">
              Hotels & venues <br /><span className="text-[#FF5238]">we partner with.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
            {VENUES.map((v) => (
              <div key={v.name} className="border-t border-white/15 pt-5">
                <div className="flex gap-3 items-start">
                  <Building2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-heading text-lg font-medium">{v.name}</div>
                    <div className="text-sm text-white/60 mt-1">{v.note}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">FAQ</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">Frequently Asked Questions</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          {FAQS.map((f, i) => (
            <div key={f.q} data-testid={`ph-faq-${i}`}>
              <h3 className="font-heading text-xl font-medium text-[#0B192C] tracking-tight">{f.q}</h3>
              <p className="text-slate-600 mt-3 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Next step</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Interested in <span className="text-[#FF5238]">the Philippines?</span>
            </h2>
            <p className="text-slate-600 mt-6 max-w-xl text-lg leading-relaxed">
              Let Trawelldmc handle everything. Request a proposal today —
              our team responds within 24 hours.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-4">
            <Link to="/inquiry?destination=Philippines" className="btn-primary w-full justify-center" data-testid="ph-bottom-cta">
              Request a proposal <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" className="btn-secondary w-full justify-center">Contact our team</Link>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16">
          <div className="overline mb-6">You may also like</div>
          <nav className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" aria-label="Related DMC destination pages">
            <Link to="/dubai-dmc" className="group flex items-start gap-3" data-testid="ph-link-dubai">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Dubai</div>
                <div className="text-sm text-slate-500 mt-1">GCC HQ · MICE & corporate</div>
              </div>
            </Link>
            <Link to="/thailand-dmc" className="group flex items-start gap-3" data-testid="ph-link-thailand">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Thailand</div>
                <div className="text-sm text-slate-500 mt-1">Bangkok · Phuket · Chiang Mai</div>
              </div>
            </Link>
            <Link to="/vietnam-dmc" className="group flex items-start gap-3" data-testid="ph-link-vietnam">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Vietnam</div>
                <div className="text-sm text-slate-500 mt-1">Hanoi · HCMC · Ha Long Bay</div>
              </div>
            </Link>
            <Link to="/" className="group flex items-start gap-3" data-testid="ph-link-home">
              <Plane className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">Trawelldmc Home</div>
                <div className="text-sm text-slate-500 mt-1">All destinations & services</div>
              </div>
            </Link>
          </nav>
        </div>
      </section>
    </div>
  );
}
